import modalService from 'layout/services/modalService';
import { trackLeadOptionSelected } from '../services/leadFormTracking';
import { closeModalSvg } from '../constants/svgIcons';
import { updateModalProgress } from '../services/taskFlow';
import GetMoreInformationForm from './getMoreInformationForm';
import RequestATourForm from './requestATourForm';

/**
 * "Request information" entry/router modal — triggered by any
 * [data-lead-generator-opener="request-information"] CTA via wrapper.js's click delegation.
 * Displays two options; Request a Tour or Get More Information.
 */
export default class RequestInformationEntry {
  constructor(properties = {}) {
    this.properties = properties;
    this.formId = 'request-information-entry';
    this.closeAttribute = '[data-close]';
    this.bookAppointmentAttribute = '[data-request-information-book-appointment]';
    this.contactSalesCenterAttribute = '[data-request-information-contact-sales-center]';
    this.onContactSalesCenterClick = null;
    this.onBookAppointmentClick = null;
  }

  /**
   * Binds the events needed so the entry modal can respond to user interactions.
   */
  bindEvents() {
    const closeElements = document.querySelectorAll(this.closeAttribute);
    closeElements.forEach((element) => element.addEventListener('click', RequestInformationEntry.closeModal));

    this.onBookAppointmentClick = (event) => {
      trackLeadOptionSelected({ buttonName: 'book_appointment', fromPage: this.properties.fromPage });
      RequestInformationEntry.openRequestATour(event);
    };
    const bookAppointmentElement = document.querySelector(this.bookAppointmentAttribute);
    bookAppointmentElement?.addEventListener('click', this.onBookAppointmentClick);

    this.onContactSalesCenterClick = (event) => {
      trackLeadOptionSelected({ buttonName: 'contact_sales_center', fromPage: this.properties.fromPage });
      RequestInformationEntry.openGetMoreInformation(event);
    };
    const contactSalesCenterElement = document.querySelector(this.contactSalesCenterAttribute);
    contactSalesCenterElement?.addEventListener('click', this.onContactSalesCenterClick);
  }

  /**
   * Unbinds the events needed so the entry modal can respond to user interactions.
   */
  unbindEvents() {
    const closeElements = document.querySelectorAll(this.closeAttribute);
    closeElements.forEach((element) => element.removeEventListener('click', RequestInformationEntry.closeModal));

    const bookAppointmentElement = document.querySelector(this.bookAppointmentAttribute);
    bookAppointmentElement?.removeEventListener('click', this.onBookAppointmentClick);

    const contactSalesCenterElement = document.querySelector(this.contactSalesCenterAttribute);
    contactSalesCenterElement?.removeEventListener('click', this.onContactSalesCenterClick);
  }

  static closeModal(event) {
    event.preventDefault();
    modalService.clearHtml();
  }

  /**
   * Swaps the already-open modal's content from this screen straight to GetMoreInformationForm —
   * same mechanism wrapper.js's showRecommendationsScreen/showThankYouScreen already use for
   * their own screen-to-screen transitions. One-way per Figma: no back button, only close (X).
   * @param {Event} event
   */
  static openGetMoreInformation(event) {
    event.preventDefault();

    const form = new GetMoreInformationForm();

    modalService.onHtmlReady({
      name: 'getMoreInformationForm',
      fn: form.bindEvents.bind(form),
    });

    modalService.onHtmlRemoved({
      name: 'getMoreInformationForm',
      fn: form.unbindEvents.bind(form),
    });

    updateModalProgress({ name: 'getMoreInformationForm', variation: 'information' });

    modalService.setHtml({ payload: form.getHtml() });
  }

  /**
   * Swaps the already-open modal's content from this screen straight to RequestATourForm — same
   * mechanism as openGetMoreInformation. One-way per Figma: no back button, only close (X).
   * @param {Event} event
   */
  static openRequestATour(event) {
    event.preventDefault();

    const form = new RequestATourForm();

    modalService.onHtmlReady({
      name: 'requestATourForm',
      fn: form.bindEvents.bind(form),
    });

    modalService.onHtmlRemoved({
      name: 'requestATourForm',
      fn: form.unbindEvents.bind(form),
    });

    updateModalProgress({ name: 'requestATourForm', variation: 'information' });

    modalService.setHtml({ payload: form.getHtml() });
  }

  /**
   * @returns the HTML that represents the "Request information" entry modal.
   */
  getHtml() {
    return `
    <div id="${this.formId}">
      <div class="nhs-o-flex-row-items-center nhs-u-gap--200 nhs-u-padding--600">
        <p class="nhs-u-flex--1-0-0 nhs-o-typography--souvenir-heading-1-extra-large-light">Request information</p>
        <button type="button" class="nhs-n1-c-button__modal-close nhs-n1-c-button__modal-close--transparent" data-close aria-label="Close modal">
          ${closeModalSvg}
        </button>
      </div>
      <div class="nhs-o-flex-column-items-start-self-stretch nhs-u-gap--600 nhs-u-padding--0-600-600">
        <button
          type="button"
          class="nhs-o-flex-column-items-start-self-stretch nhs-u-gap--100 nhs-u-padding--500 nhs-u-background-color--white-100 nhs-u-border--25-black-700 nhs-u-radius--200 nhs-u-cursor--pointer nhs-u-text--align-left"
          data-request-information-book-appointment
          data-qa="request-information-book-appointment"
        >
          <span class="nhs-o-typography--sans-subhead-1-large-medium nhs-u-color--black-900">Book appointment</span>
          <span class="nhs-o-typography--sans-body-1-small-book nhs-u-color--black-900">Make a request to tour this community</span>
        </button>
        <button
          type="button"
          class="nhs-o-flex-column-items-start-self-stretch nhs-u-gap--100 nhs-u-padding--500 nhs-u-background-color--white-100 nhs-u-border--25-black-700 nhs-u-radius--200 nhs-u-cursor--pointer nhs-u-text--align-left"
          data-request-information-contact-sales-center
          data-qa="request-information-contact-sales-center"
        >
          <span class="nhs-o-typography--sans-subhead-1-large-medium nhs-u-color--black-900">Contact sales center</span>
          <span class="nhs-o-typography--sans-body-1-small-book nhs-u-color--black-900">Request details on prices and floorplans.</span>
        </button>
      </div>
    </div>`;
  }
}
